import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { RECORD_DATA_TYPE, FINALIZATION_STATUS, formatDateTime } from '@demandproai/shared';
import useAxios from './useAxios';
import usePfqAxios from './usePfqAxios';
import useFinalizeLifecareUpload from './useFinalizeLifecareUpload';
import useLifeCarePlanSupportingDocuments from './useLifeCarePlanSupportingDocuments';
import useCaseRecordDelete from './useCaseRecordDelete';
import useLifeCarePlanReassignment from './useLifeCarePlanReassignment';
import Constants from '../Constants';
import { toaster } from '../utils/toast';
import { fileUploadingBtnToolTipMsg, missingPlaintiffFilesTooltipMsg, finalizePlanBtnLabel } from '../utils/constant';
import { prepFileDataForDedicatedUpload } from '../utils/helper';

const useFinalizeLifecareSummary = () => {
    const { caseId } = useParams();
    const [caseData, setCaseData] = useState({});
    const [selectedClientIndex, setSelectedClientIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [removingFileId, setRemovingFileId] = useState(null);
    const [finalizationData, setFinalizationData] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingReplaceFiles, setPendingReplaceFiles] = useState(null);
    const { getData, isLoading } = useAxios();
    const { getData: getFinalizationData } = useAxios();
    const { postData } = useAxios();
    const { postData: postPfqData } = usePfqAxios();
    const { removeFileRecords } = useCaseRecordDelete();
    const { isLoading: isUploading, isUploadPending, dedicatedFileUpload } = useFinalizeLifecareUpload({ caseId });

    useEffect(() => {
        if (!caseId) return;
        const fetchCase = async () => {
            const response = await getData(`${Constants.ApiUrl.caseSummary.getCaseSummary}/${caseId}`);
            if (response?.success) setCaseData(response?.data ?? {});
        };
        fetchCase();
    }, [caseId, getData]);

    // F5-safe: the finalization record carries its own status, read once on load
    useEffect(() => {
        if (!caseId) return;
        const fetchFinalization = async () => {
            const response = await getFinalizationData(`${Constants.ApiUrl.certifiedLifecarePlan.finalize}/${caseId}`);
            if (response?.success && response?.data?.finalization) {
                setFinalizationData(response.data.finalization);
            }
        };
        fetchFinalization();
    }, [caseId, getFinalizationData]);

    const isAlreadyFinalized = !!finalizationData;
    const hasFailedFinalization = finalizationData?.status === FINALIZATION_STATUS.FAILED;
    const finalizationLabel = useMemo(() => {
        if (!finalizationData?.createdAt) return null;
        const who = finalizationData?.finalizedBy || 'Unknown';
        return `Finalized by ${who} at ${formatDateTime(finalizationData.createdAt)}`;
    }, [finalizationData]);

    // two-step handoff: sync snapshot (blocks behind the overlay), then fire-and-forget the async zip build
    const handleFinalize = useCallback(async () => {
        setIsConfirmModalOpen(false);
        setIsSubmitting(true);
        try {
            const response = await postData(
                `${Constants.ApiUrl.certifiedLifecarePlan.finalizeSnapshot}/${caseId}`,
                { override: isAlreadyFinalized }
            );
            if (!response?.success) {
                toaster({ message: response?.message || 'Failed to start finalization', success: false });
                return;
            }
            const { finalizationId } = response.data ?? {};
            // do not await: the planner is freed while the zip is packaged in the background
            postPfqData(`${Constants.ApiUrl.certifiedLifecarePlan.finalize}/${caseId}`, { finalizationId });
            // mark a finalization as present so the next click routes through the re-finalize confirmation
            setFinalizationData((prev) => ({ ...(prev ?? {}), status: FINALIZATION_STATUS.IN_PROGRESS }));
            toaster({ message: 'Finalization started. This may take a moment.', success: true });
        } finally {
            setIsSubmitting(false);
        }
    }, [caseId, postData, postPfqData, isAlreadyFinalized]);

    const requestFinalize = useCallback(() => {
        if (isAlreadyFinalized) return setIsConfirmModalOpen(true);
        handleFinalize();
    }, [isAlreadyFinalized, handleFinalize]);

    const cancelFinalize = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    const handleRemoveFile = useCallback(async (file) => {
        const fileKey = file?.s3UrlPath || file?.uniqueKey;
        if (!fileKey) return;

        setRemovingFileId(fileKey);
        try {
            await removeFileRecords({
                filesToDelete: [file],
                caseRecordType: file?.metaData?.caseRecordType,
                data: { [`clients.${selectedClientIndex}.damage.certifiedLifecarePlan`]: { s3UrlPath: fileKey } },
            });
            setCaseData((prev) => {
                const clients = prev?.clients?.map((c, i) => {
                    if (i !== selectedClientIndex) return c;
                    return {
                        ...c,
                        damage: {
                            ...c.damage,
                            certifiedLifecarePlan: c?.damage?.certifiedLifecarePlan?.filter(
                                (f) => (f?.s3UrlPath || f?.uniqueKey) !== fileKey
                            ) ?? [],
                        },
                    };
                }) ?? [];
                return { ...prev, clients };
            });
            toaster({ message: 'File removed successfully', success: true });
        } catch {
            toaster({ message: 'Failed to remove file', success: false });
        } finally {
            setRemovingFileId(null);
        }
    }, [removeFileRecords, selectedClientIndex]);

    const uploadCertifiedFiles = useCallback(async (files) => {
        const fieldKey = `clients.${selectedClientIndex}.damage.certifiedLifecarePlan`;
        const prepFiles = prepFileDataForDedicatedUpload(files);
        const uploadedFiles = await dedicatedFileUpload({
            files: prepFiles,
            caseRecordType: RECORD_DATA_TYPE.CERTIFIED_LIFECARE_PLAN,
            updateCasePayload: { fieldKey, action: 'add' },
        });
        setCaseData((prev) => {
            const clients = prev?.clients?.map((c, i) => {
                if (i !== selectedClientIndex) return c;
                return {
                    ...c,
                    damage: {
                        ...c.damage,
                        certifiedLifecarePlan: [...(c?.damage?.certifiedLifecarePlan ?? []), ...uploadedFiles],
                    },
                };
            }) ?? [];
            return { ...prev, clients };
        });
    }, [dedicatedFileUpload, selectedClientIndex]);

    // Single-file mode: when exactly one file exists, route through the replace-confirmation modal; legacy cases with >1 files keep the additive behavior
    const handleFileSelection = useCallback(async ({ file: files }) => {
        if (!files?.length) return;
        const current = caseData?.clients?.[selectedClientIndex]?.damage?.certifiedLifecarePlan ?? [];
        if (current.length === 1) {
            setPendingReplaceFiles(files);
            return;
        }
        await uploadCertifiedFiles(files);
    }, [caseData, selectedClientIndex, uploadCertifiedFiles]);

    const confirmReplaceCertifiedFile = useCallback(async () => {
        if (!pendingReplaceFiles) return;
        const current = caseData?.clients?.[selectedClientIndex]?.damage?.certifiedLifecarePlan ?? [];
        const existingFile = current[0];
        const filesToUpload = pendingReplaceFiles;
        setPendingReplaceFiles(null);
        if (existingFile) {
            await handleRemoveFile(existingFile);
        }
        await uploadCertifiedFiles(filesToUpload);
    }, [pendingReplaceFiles, caseData, selectedClientIndex, handleRemoveFile, uploadCertifiedFiles]);

    const cancelReplaceCertifiedFile = useCallback(() => {
        setPendingReplaceFiles(null);
    }, []);

    const handleReassigned = useCallback((newUser) => {
        setCaseData((prev) => ({ ...prev, userId: newUser }));
    }, []);

    const handleSupportingDocsAdded = useCallback((uploadedFiles) => {
        setCaseData((prev) => ({
            ...prev,
            lifeCarePlanSupportingDocuments: [
                ...(prev?.lifeCarePlanSupportingDocuments ?? []),
                ...uploadedFiles,
            ],
        }));
    }, []);

    const handleSupportingDocRemoved = useCallback((fileKey) => {
        setCaseData((prev) => ({
            ...prev,
            lifeCarePlanSupportingDocuments: (prev?.lifeCarePlanSupportingDocuments ?? []).filter(
                (f) => (f?.s3UrlPath || f?.uniqueKey) !== fileKey
            ),
        }));
    }, []);

    const reassignment = useLifeCarePlanReassignment({ caseId, currentOwner: caseData?.userId, onReassigned: handleReassigned });

    const clients = caseData?.clients ?? [];
    const selectedClient = clients?.[selectedClientIndex];
    const files = selectedClient?.damage?.certifiedLifecarePlan ?? [];
    const supportingDocuments = caseData?.lifeCarePlanSupportingDocuments ?? [];
    const supportingDocs = useLifeCarePlanSupportingDocuments({
        caseId,
        supportingDocuments,
        onAdd: handleSupportingDocsAdded,
        onRemove: handleSupportingDocRemoved,
    });
    const hasAllClientsFiles = clients.length > 0
        && clients.every((c) => c?.damage?.certifiedLifecarePlan?.length > 0);
    const isAnyUploadPending = isUploadPending || supportingDocs.isUploading;
    const shouldShowTooltip = isAnyUploadPending || !hasAllClientsFiles;
    const tooltipTitle = isAnyUploadPending ? fileUploadingBtnToolTipMsg : missingPlaintiffFilesTooltipMsg;
    const isDisabled = isSubmitting || shouldShowTooltip;

    const getFinalizeButtonLabel = () => {
        if (isSubmitting) return finalizePlanBtnLabel.submitting;
        if (isAlreadyFinalized) return finalizePlanBtnLabel.reFinalize;
        return finalizePlanBtnLabel.finalize;
    };
    const finalizeButtonLabel = getFinalizeButtonLabel();

    return {
        caseId,
        caseData,
        clients,
        files,
        selectedClientIndex,
        setSelectedClientIndex,
        isLoading,
        isSubmitting,
        isUploading,
        removingFileId,
        finalizationLabel,
        hasFailedFinalization,
        isConfirmModalOpen,
        isDisabled,
        shouldShowTooltip,
        tooltipTitle,
        finalizeButtonLabel,
        requestFinalize,
        handleFinalize,
        cancelFinalize,
        handleRemoveFile,
        handleFileSelection,
        reassignment,
        supportingDocs,
        isReplaceModalOpen: pendingReplaceFiles !== null,
        confirmReplaceCertifiedFile,
        cancelReplaceCertifiedFile,
    };
};

export default useFinalizeLifecareSummary;
